import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PreloadingStrategy, Route } from '@angular/router';
import { catchError, Observable, of } from 'rxjs';

// Key used in route data to opt a route into preloading.
// Example: { path: 'some-path', loadComponent: ..., data: { preload: true } }
export const PRELOAD_ROUTE_DATA_KEY = 'preload';

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

// Preloads only routes explicitly opted in via PRELOAD_ROUTE_DATA_KEY,
// instead of PreloadAllModules downloading every lazy chunk in the app
// right after bootstrap. See todos/optimization.md item 8.
@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  private readonly browser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.browser = isPlatformBrowser(platformId);
  }

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // A server render has no module map to warm and no next navigation to be
    // ready for, so preloading there is pure cost: `load()` would pull a lazy
    // chunk into the render for nobody, and the pending import would hold the
    // zone open while a prerender waits for it to fall quiet.
    //
    // `RouterPreloader` walks the whole config on the first NavigationEnd, so
    // this runs on every route, not only the opted-in ones below.
    if (!this.browser) {
      return of(null);
    }
    if (route.data?.[PRELOAD_ROUTE_DATA_KEY] !== true) {
      return of(null);
    }
    if (this.isSlowConnection()) {
      return of(null);
    }
    // Nothing speculative is worth spending the chunk's one chance to load.
    // A failed `import()` is recorded by the browser's module map for the life
    // of the document, so a preload that drops while the visitor is offline
    // poisons a route they have not even asked for yet - and `lazyChunk` then
    // has to buy it back with a cache-busted retry when they do click it.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return of(null);
    }
    return load().pipe(
      // A preload is best-effort, and `RouterPreloader` subscribes to this
      // with no error handler, so an error here does not just lose one chunk:
      // it completes that subscription and nothing preloads again for the
      // rest of the session. Swallowed, the next NavigationEnd tries again.
      catchError((error) => {
        console.warn(
          `[preload] route "${route.path ?? '(matcher)'}" failed to preload`,
          error
        );
        return of(null);
      })
    );
  }

  /**
   * Only ever called on the browser path above, but it reads the global
   * defensively anyway: `navigator` is absent on node below 21 (node 24 does
   * define one, which is why this crashed for some developers and not others),
   * and an unguarded global here is what made a missing one a ReferenceError
   * rather than "no connection info". The `navigator.onLine` check below
   * already had the guard; this one did not.
   */
  private isSlowConnection(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const connection = (
      navigator as unknown as { connection?: NetworkInformation }
    ).connection;
    if (!connection) {
      return false;
    }
    if (connection.saveData) {
      return true;
    }
    return (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g'
    );
  }
}
